import { Injectable, signal } from '@angular/core';

export type MobileTheme = 'light' | 'dark';

const STORAGE_KEY = 'sovereign-theme';
const TRANSITION_MS = 280;

/**
 * Theme service aligned with web ThemeService — shared localStorage key.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly theme = signal<MobileTheme>(this.readInitialTheme());

  readonly currentTheme = this.theme.asReadonly();

  constructor() {
    this.apply(this.theme(), false);
  }

  toggle(): void {
    this.setTheme(this.theme() === 'light' ? 'dark' : 'light');
  }

  setTheme(next: MobileTheme): void {
    this.apply(next, true);
  }

  private readInitialTheme(): MobileTheme {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return 'light';
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }

  private apply(next: MobileTheme, animate: boolean): void {
    this.theme.set(next);
    const root = document.documentElement;

    if (animate) {
      root.classList.add('theme-transition');
      window.setTimeout(() => root.classList.remove('theme-transition'), TRANSITION_MS);
    }

    if (next === 'dark') {
      root.setAttribute('data-theme', 'dark');
      root.classList.add('dark');
      root.style.colorScheme = 'dark';
    } else {
      root.removeAttribute('data-theme');
      root.classList.remove('dark');
      root.style.colorScheme = 'light';
    }

    localStorage.setItem(STORAGE_KEY, next);
  }
}
